
// Core Web Vitals tracking and Google Analytics integration
// This module provides functions for tracking key user interactions and page performance

/**
 * Initialize Google Analytics
 * Call this function once when the app first loads
 * @param measurementId The Google Analytics measurement ID (format: G-XXXXXXXXXX)
 */
export const initializeAnalytics = (measurementId: string): void => {
  try {
    // Create script elements for Google Analytics
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    
    // Initialize gtag
    const inlineScript = document.createElement('script');
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}', { 
        send_page_view: false, 
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false
      });
    `;
    
    // Add scripts to document
    document.head.appendChild(gtagScript);
    document.head.appendChild(inlineScript);
    
    console.log('Google Analytics initialized');
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Track a page view in Google Analytics
 * @param path The current page path
 * @param title The page title
 */
export const trackPageView = (path: string, title: string): void => {
  try {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title,
        page_location: window.location.href,
        custom_map: {
          'dimension1': 'user_journey_stage'
        }
      });
    }
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

/**
 * Track a custom event in Google Analytics
 * @param eventName Name of the event to track
 * @param eventParams Additional parameters for the event
 */
export const trackEvent = (eventName: string, eventParams?: Record<string, any>): void => {
  try {
    if (window.gtag) {
      window.gtag('event', eventName, {
        ...eventParams,
        event_category: 'engagement',
        send_to: 'GA_MEASUREMENT_ID'
      });
    }
  } catch (error) {
    console.error(`Error tracking event ${eventName}:`, error);
  }
};

/**
 * Track Core Web Vitals metrics
 * Monitors and reports LCP, FID, and CLS metrics
 */
export const trackCoreWebVitals = (): void => {
  if ('PerformanceObserver' in window) {
    try {
      // Track Largest Contentful Paint (LCP)
      new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          const metricName = 'LCP';
          const value = entry.startTime;
          console.log(`${metricName}: ${value}ms`);
          
          // Only send after onload completes
          if (document.readyState === 'complete') {
            trackEvent('web_vitals', { 
              metric_name: metricName, 
              metric_value: Math.round(value), 
              metric_rating: value < 2500 ? 'good' : value < 4000 ? 'needs-improvement' : 'poor'
            });
          }
        }
      }).observe({ type: 'largest-contentful-paint', buffered: true });
      
      // Track First Input Delay (FID)
      new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          const metricName = 'FID';
          const fidEntry = entry as PerformanceEventTiming;
          const value = fidEntry.processingStart - fidEntry.startTime;
          console.log(`${metricName}: ${value}ms`);
          
          trackEvent('web_vitals', { 
            metric_name: metricName, 
            metric_value: Math.round(value),
            metric_rating: value < 100 ? 'good' : value < 300 ? 'needs-improvement' : 'poor'
          });
        }
      }).observe({ type: 'first-input', buffered: true });
      
      // Track Cumulative Layout Shift (CLS)
      let clsValue = 0;
      let clsEntries: PerformanceEntry[] = [];
      
      new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          const layoutShift = entry as LayoutShift;
          
          if (!layoutShift.hadRecentInput) {
            const firstSessionEntry = clsEntries.length === 0;
            const currentTime = entry.startTime;
            
            if (firstSessionEntry || 
                currentTime - clsEntries[clsEntries.length - 1].startTime < 1000 && 
                currentTime - clsEntries[0].startTime < 5000) {
              clsEntries.push(entry);
            } else {
              clsEntries = [entry];
            }
            
            let sessionValue = 0;
            clsEntries.forEach(entry => {
              const layoutShiftEntry = entry as LayoutShift;
              sessionValue += layoutShiftEntry.value;
            });
            
            if (sessionValue > clsValue) {
              clsValue = sessionValue;
              console.log(`CLS update: ${clsValue}`);
              
              if (document.readyState === 'complete') {
                trackEvent('web_vitals', { 
                  metric_name: 'CLS', 
                  metric_value: Math.round(clsValue * 1000) / 1000,
                  metric_rating: clsValue < 0.1 ? 'good' : clsValue < 0.25 ? 'needs-improvement' : 'poor'
                });
              }
            }
          }
        }
      }).observe({ type: 'layout-shift', buffered: true });
      
      console.log('Core Web Vitals monitoring initialized');
    } catch (error) {
      console.error('Error setting up Core Web Vitals tracking:', error);
    }
  }
};

/**
 * Track user engagement metrics
 */
export const trackUserEngagement = (): void => {
  let engagementTime = 0;
  let isActive = true;
  
  // Track time on page
  const startTime = Date.now();
  
  const trackEngagement = () => {
    if (isActive) {
      engagementTime = Date.now() - startTime;
      trackEvent('user_engagement', {
        engagement_time_msec: engagementTime,
        page_path: window.location.pathname
      });
    }
  };
  
  // Track engagement every 15 seconds
  setInterval(trackEngagement, 15000);
  
  // Track when user becomes inactive
  document.addEventListener('visibilitychange', () => {
    isActive = !document.hidden;
    if (!isActive) {
      trackEngagement();
    }
  });
  
  // Track before page unload
  window.addEventListener('beforeunload', trackEngagement);
};
